/**
 * Component (consumable) defects on the job queue — pure helpers.
 *
 * A purchased component (a `consumables` row: rivnuts, inserts, hardware) used
 * on an assembly can be destroyed at the join station. The API's consumable
 * branch of POST /defects (jobq-api `_record_consumable_defect`) writes the
 * quality row AND takes the pieces out of consumable stock; these helpers build
 * that call and describe its answer. Kept out of JobQueuePage.jsx so they can be
 * unit-tested without mounting an 8k-line page.
 */

/** Same-job duplicate-entry window (mirrors the part-defect guard). */
export const DUP_WINDOW_MS = 14 * 86400 * 1000;

export function isConsumableDefect(d) {
  return !!d && (d.defect_type === 'consumable' || d.consumable_id != null);
}

/**
 * Component defects already on this job for the same consumable, inside the
 * window. Decoupled rows (task_id NULL) never show on task rows, which is how
 * the same damage got entered twice on SO-1102 — so the caller confirms first.
 */
export function recentConsumableDefects(defects, consumableId, now = Date.now()) {
  if (!Array.isArray(defects) || consumableId == null) return [];
  return defects
    .filter((d) => isConsumableDefect(d) && Number(d.consumable_id) === Number(consumableId))
    .filter((d) => {
      const t = new Date(d.created_at).getTime();
      return Number.isFinite(t) && now - t < DUP_WINDOW_MS;
    });
}

export function describeRecentDefects(list, now = Date.now()) {
  return list.slice(0, 4).map((d) => {
    const hrs = Math.round((now - new Date(d.created_at).getTime()) / 3600000);
    const age = hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
    return `• ${d.defective_quantity} pc(s) by ${d.recorded_by_name || '?'} ${age} — "${(d.defect_reason || '').slice(0, 60)}"`;
  }).join('\n');
}

/**
 * How many of a component THIS job consumes. Prefer the job-scoped BOM row
 * (GET /assemblies/{id}/bom — the same payload the printed report uses); fall
 * back to qty/assembly × assemblies on the task, where the join task's quantity
 * is in primary-part space and must be divided by primary_part_quantity first
 * (the ppq convention — forgetting it multiplies by ppq).
 */
export function consumableJobQty({ bomRow, quantityPerAssembly, taskQuantity, primaryPartQuantity }) {
  if (bomRow && bomRow.job_qty != null && Number.isFinite(Number(bomRow.job_qty))) {
    return Number(bomRow.job_qty);
  }
  const qpa = Number(quantityPerAssembly);
  const tq = Number(taskQuantity);
  if (!Number.isFinite(qpa) || !Number.isFinite(tq)) return null;
  const ppq = Math.max(1, Number(primaryPartQuantity) || 1);
  return Math.floor(tq / ppq) * qpa;
}

/** A whole number of pieces, at least 1; null for anything else (blank, 0, "abc"). */
export function parseDefectQty(str) {
  const n = parseInt(str, 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

export function buildConsumableDefectPayload({ jobId, taskId, workstation, consumableId, qty, reason, pin }) {
  return {
    job_id: jobId,
    task_id: taskId,
    workstation,
    consumable_id: consumableId,
    defective_quantity: qty,
    defect_reason: reason,
    defect_type: 'consumable',
    pin,
  };
}

/** One line for the operator: what was recorded and where stock landed. */
export function describeConsumableDefectResult(json, fallbackProductId) {
  const pid = (json && json.product_id) || fallbackProductId || 'component';
  const qty = json && json.defective_quantity;
  if (json && json.stock_before != null && json.stock_after != null) {
    return `Recorded ${qty} × ${pid} as defective. Stock ${json.stock_before} → ${json.stock_after}.`;
  }
  return (json && json.message) || `Recorded ${qty != null ? qty : ''} × ${pid} as defective.`.replace('  ', ' ');
}
